export interface IResponseSuccess {
    statusCode: number;
    message: string;
}

export interface IResponseSession {
    session_id: string;
    branch_id: number;
    ip_address: string;
    action: string;
    date_action: string;
    done: boolean;
    id: string;
    pocket_key: string;
}

export interface IResponseReason {
    id: number;
    code: string;
    name_en: string;
    name_th: string;
    date_add: string;
    is_default: boolean;
    active: boolean;
}

export interface IResponseBranch {
    id: number;
    code: string;
    key: string;
    name: string;
    open_status: string;
    address: string;
    order: number;
    paper_left: number;
    is_show: boolean;
    is_global: boolean;
    deleted: boolean;
}

export interface IResponseBranchProduct {
    id: number;
    branch_id: number;
    branch_code: string;
    product_code: string;
    product_name: string;
    product_desc: string;
    product_unit: string;
    img_path: string;
    price: number;
    vat_percent: number;
    can_use_discount: boolean;
}

export interface IResponseBranchUserInterface {
    id: number;
    branch_id: number;
    home_background_color: string;
    home_background_text_color: string;
    home_background_image_url: string;
    button_background_color: string;
    button_border_color: string;
    button_border_width: string;
    button_text_color: string;
    button_text_stroke_color: string;
    button_text_stroke_width: string;
    close_button_image_url: string;
    sculpture_logo_image_url: string;
    ready_background_image_url: string;
}

export interface IRequestPayment {
    branch_code: string;
    payment_type: string;
    channel: string;
    amount_net: number;
    amount_vat: number;
    description: string;
    amount_discount: number;
    currency: string;
    phone_number?: string;
    title?: string;
    ref_id_1?: string;
    ref_id_2?: string;
    ref_id_3?: string;
    ref_id_4?: string;
}

/** POST /application/payment/:trx_id/verify — `source: 'db'` means Omise was not (or could not be) asked. */
export interface IResponseVerifyPayment {
    is_paid: boolean;
    status: 'paid' | 'pending' | 'failed';
    source: 'omise' | 'db';
}

export interface IResponsePayment {
    trx_id: string;
    branch_id: number;
    payment_type: string;
    channel: string;
    amount_net: string;
    amount_vat: string;
    amount_fee: string;
    amount_fee_vat: string;
    voucher_id: string;
    amount_discount: string;
    currency: string;
    date_created: string;
    is_paid: boolean;
    date_paid_server: string;
    date_paid_local: string;
    is_cancelled: boolean;
    date_cancelled: string;
    is_refunded: boolean;
    date_refunded: string;
    ref_id_1: string;
    ref_id_2: string;
    ref_id_3: string;
    ref_id_4: string;
    id: number;
    /** CREDITCARD only: charge attempts made from the hosted card page. */
    attempt_count?: number;
}

export interface IResponseDslrStart {
    ApiVersion: number;
    Command: string;
    IsSuccessful: boolean;
    ErrorMessage: string;
}

export interface IResponsePromotion {
    statusCode: number;
    message: string;
    data: {
        id: number;
        branch_id: number;
        code: string;
        note: string;
        is_used: boolean;
        date_used: string;
        is_revoke: boolean;
        is_give: boolean;
        date_revoke: string;
        date_create: string;
        date_expire: string;
        date_give: string;
        code_type: string;
        code_amount: number;
        code_used: number;
        promo_type: string;
        promo_amount: number;
        payment_id: number;
        branch_allow: BranchPromotionAllow[];
    }
}

export interface BranchPromotionAllow {
    id: number;
    promotion_id: number;
    branch_id: number;
    date_created: string;

}

export interface IResponseGeneral {
    statusCode: number;
    message: string;
    error?: string;
}

export interface IResponsePaymentType {
    id: number;
    code: string;
    name: string;
    name_th: string;
    open: boolean;
    main: boolean;
    deleted: boolean;
}

export interface IResponsePocketBasePayment {
    page: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
    items: PaymentItem[];
}

export interface PaymentItem {
    "@collectionId": string;
    "@collectionName": string;
    charge_id: string;
    created: string;
    id: string;
    key: string;
    link_id: string;
    source_id: string;
    status: string;
    updated: string;
}

// ---- Staff PIN payment bypass and approval (sc-screenlock-v2-api 1.10.0) ----
// Shapes from sc-screenlock-v2-api docs/staff-pin-bypass-contract.md §0 and §2.

export type StaffPinPurpose = 'PAYMENT_BYPASS' | 'STAFF_MENU';

/** HiddenMenuMode minus NONE. */
export type StaffMenuAction = 'CONFIG' | 'EXIT' | 'LOGOUT';

export type BypassRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED' | 'NO_APPROVER';

export interface IBypassReason {
    id: number;
    code: string;
    label_th: string;
    label_en: string;
    sort_order: number;
    active: boolean;
}

export interface IBypassRequest {
    id: number;
    status: BypassRequestStatus;
    staff_nickname: string;
    reason_id: number;
    reason_label_th: string;
    /** PAYMENT_BYPASS count today at this branch, before this request. */
    used_today: number;
    limit: number;
    created_at: string;
    /** created_at + 5 min. */
    expires_at: string;
    decided_at: string | null;
    decided_by_name: string | null;
    note_label_th: string | null;
    note_text: string | null;
    pin_use_id: number | null;
}

export interface IStaffPinCheck {
    staff: { id: number; nickname: string; firstname: string };
    is_global: boolean;
    // Only for purpose = PAYMENT_BYPASS:
    approval_on?: boolean;
    used_today?: number;
    /** null when no limit applies (Global staff, or approval off). */
    limit?: number | null;
    /** null when no limit applies. */
    remaining?: number | null;
    needs_approval?: boolean;
    approver_available?: boolean;
}

export interface IResponseStaffPinCheck {
    statusCode: number;
    message: string;
    data: IStaffPinCheck;
}

export interface IResponseBypassReasons {
    statusCode: number;
    message: string;
    data: IBypassReason[];
}

/** POST /bypass and POST /staff-menu. */
export interface IResponsePinUse {
    statusCode: number;
    message: string;
    data: { pin_use_id: number };
}

export interface IResponseBypassRequest {
    statusCode: number;
    message: string;
    data: { request: IBypassRequest };
}

/** GET /bypass/pin/lockout (contract §10.3): this app session's PIN lockout. */
export interface IResponsePinLockout {
    statusCode: number;
    message: string;
    data: {
        /** True only while the lockout is still running on the server. */
        locked: boolean;
        locked_until: string | null;
    };
}

/** The contract's error envelope. Clients branch on `code`, never on `message`. */
export interface IApiErrorBody {
    statusCode: number;
    message: string;
    code: string;
    data?: {
        attempts_left?: number;
        locked_until?: string;
        used_today?: number;
        limit?: number;
    };
}

export interface IResponsePointBalance {
    statusCode: number;
    message: string;
    data: PointBalanceData;
}

export interface PointBalanceData {
    phone_no_masked: string;
    point_balance: number;
    point_list: PointList[];
}

export interface PointList {
    id: string;
    member_id: number;
    earned_at: Date;
    expires_at: Date;
    redemption_id: null;
    source: string;
    payment_id: string;
}

export interface IRequestPointEarn {
    phone_no: string;
    birth_date?: string;
    gender?: string;
    source: string;
    payment_trx: string;
}

export interface IResponsePointEarn {
    statusCode: number;
    message: string;
    data: PointEarnData;
}

export interface PointEarnData {
    member_id: number;
    earned_at: string;
    expires_at: string;
    source: string;
    payment_id: number;
    redemption_id: null;
    id: string;
}

export interface IRequestPointRedeem {
    phone_no: string;
    points_used: number;
    redeem_type: string;
    branch_id: number;
}

export interface IResponsePointRedeem {
    statusCode: number;
    message: string;
    data: PointRedeemData;
}

export interface PointRedeemData {
    member_id: number;
    points_used: number;
    created_at: string;
    redeem_type: string;
    branch_id: number;
    promotion_id: number;
    id: string;
    promotion_detail: PromotionDetail;
}

export interface PromotionDetail {
    note: string;
    code: string;
    date_expire: string;
}

// ── Banner popup (/home promotional campaigns) ──────────────────────────────

export interface IBannerSlide {
    id: number;
    sort_order: number;
    landscape_url: string;
    portrait_url: string | null;
}

export interface IBannerCampaign {
    id: number;
    name: string;
    priority: number;
    slides: IBannerSlide[];
}

export interface IResponseActiveBanners {
    statusCode: number;
    message: string;
    data: IBannerCampaign[];
}

export type BannerEventName = 'shown' | 'slide_view' | 'closed';

export interface IRequestBannerEvent {
    impression_id: string;
    campaign_id: number;
    branch_id: number;
    event: BannerEventName;
    slide_index?: number;
    close_reason?: string;
    kiosk_time: string;
}
